/**
 * CSV helpers for un-bulk-upload (PRD §7 — both downloads are purely
 * client-side base64/Blob, no network).
 *
 * - `readFileAsBase64` turns the picked `File` into the base64 string the
 *   upload request expects (no data-URL prefix).
 * - `downloadTextFile` / `downloadBase64File` trigger a client-side download
 *   (the "Download example file" link and the "Download error report" button).
 * - `EXAMPLE_CSV` is the embedded sample file matching the CSV row contract
 *   (PRD §1), mirroring `flows/un-bulk-upload/exampleBulkUploadFileWithOneLine_2206.csv`.
 */

/** Reads a `File` to a base64 string (data-URL prefix stripped). */
export const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });

const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  // Defer cleanup: revoking the object URL synchronously right after click()
  // can cancel the download before the browser has read the blob.
  setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, 0);
};

/** Download a plain UTF-8 text/CSV string as a file. */
export const downloadTextFile = (filename: string, text: string): void => {
  triggerDownload(new Blob([text], { type: 'text/csv;charset=utf-8' }), filename);
};

/**
 * Download a base64-encoded CSV as a file. Decodes to raw bytes (not via a
 * data-URL string) so UTF-8 content — e.g. delegate names with accents in the
 * error report — round-trips intact.
 *
 * Tolerates whitespace and URL-safe base64, and returns `false` (instead of
 * throwing an uncaught `DOMException`) when the input can't be decoded, so the
 * caller can surface a toast rather than failing silently.
 */
export const downloadBase64File = (filename: string, base64: string): boolean => {
  const normalised = base64.replace(/\s/g, '').replace(/-/g, '+').replace(/_/g, '/');
  let binary: string;
  try {
    binary = atob(normalised);
  } catch {
    return false;
  }
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  triggerDownload(new Blob([bytes], { type: 'text/csv;charset=utf-8' }), filename);
  return true;
};

export const EXAMPLE_CSV_FILENAME = 'bulk-upload-example.csv';
export const ERROR_REPORT_FILENAME = 'bulk-upload-error-report.csv';

/** Embedded sample CSV (PRD §7) — header + two example delegate rows. */
export const EXAMPLE_CSV = `Index Number,First Name,Last Name,Name on card,Email,Phone number,Phone Country,Birth Date,Country,Currency,Card Type,Payment Amount
A0001,John,Doe,John Doe,john.doe@example.com,1234567890,GB,15/04/1985,GB,GBP,PHYSICAL,100.50
A0002,Jane,Doe,Jane Doe,jane.doe@example.com,1234567890,GB,15/04/1985,GB,USD,PHYSICAL,100.50
`;
